import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { LoaderSpin, Section, Text } from 'components';
import { useAppContext } from 'contexts/app';
import { routeConstants } from 'routes';
import { clearRequest, selectAPIRequestStatus } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { getPosEmployeesRequest, POS, selectPOSEmployees } from 'store/Collections';
import { Integrations, listSiteIntegrationsRequest, selectSiteIntegrations } from 'store/Collections/Integrations';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { getIntegrationLocationRequest, Integration, selectIntegrationLocation } from 'store/Integration';
import { getLaborRequest, getPosJobTitlesRequest, LaborReview as LaborReviewActionTypes, selectLaborLocations, selectLaborRecords, selectPosJobTitles } from 'store/LaborReview';
import { IStore } from 'store/types';
import { IntegrationStatus, IntegrationType, PermissionType, RequestStatus } from 'types';
import { formatDateForApi } from 'utils/date';
import { hasSitePermission } from 'utils/employers';
import { LaborReviewBoard } from './LaborReviewBoard';
import { LaborReviewLoadError } from './LaborReviewLoadError';
import { LoadingContainer } from './styles';
import { getUniqueJobTitles } from './utils';

// Every request this screen depends on.
const LABOR_REVIEW_REQUESTS = [
  Integrations.LIST_SITE_INTEGRATIONS_REQUEST,
  POS.GET_POS_EMPLOYEES_REQUEST,
  LaborReviewActionTypes.GET_POS_JOB_TITLES_REQUEST,
  Integration.GET_INTEGRATION_LOCATION_REQUEST,
  LaborReviewActionTypes.GET_LABOR_REQUEST,
];

const LaborReview = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();

  const appContext = useAppContext();

  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentSiteId = currentEmployee?.siteId;
  const currentSite = useSelector(selectCurrentEmployeeSite);

  const hasLaborDataViewPermission = hasSitePermission(currentSite?.permissions, PermissionType.INTEGRATION_POS_LABOR_DATA_VIEW);

  // The integrations request status and cache are shared app-wide, so until this screen has requested the current site's
  // integrations they can only hold another screen's (or another site's) results, which must not count as loaded.
  const [integrationsRequestedFor, setIntegrationsRequestedFor] = useState<string>();
  const integrationsStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Integrations.LIST_SITE_INTEGRATIONS_REQUEST));

  const siteIntegrations = {
    data: useSelector(selectSiteIntegrations(currentSiteId || '')),
    status: currentSiteId && integrationsRequestedFor === currentSiteId ? integrationsStatus : RequestStatus.UNINITIATED,
  };

  const posLabor = {
    data: useSelector(selectLaborRecords()),
    locations: useSelector(selectLaborLocations()),
    status: useSelector((state: IStore) => selectAPIRequestStatus(state, LaborReviewActionTypes.GET_LABOR_REQUEST)),
  };

  const posEmployees = {
    data: useSelector(selectPOSEmployees()) || [],
    status: useSelector((state: IStore) => selectAPIRequestStatus(state, POS.GET_POS_EMPLOYEES_REQUEST)),
  };

  const posJobs = {
    data: useSelector(selectPosJobTitles()),
    status: useSelector((state: IStore) => selectAPIRequestStatus(state, LaborReviewActionTypes.GET_POS_JOB_TITLES_REQUEST)),
  };

  const posLocation = {
    data: useSelector(selectIntegrationLocation()),
    status: useSelector((state: IStore) => selectAPIRequestStatus(state, Integration.GET_INTEGRATION_LOCATION_REQUEST)),
  };

  const jobTitles = useMemo(() => getUniqueJobTitles({ laborRecords: posLabor.data, jobs: posJobs.data }), [posLabor.data, posJobs.data]);

  const cinemarkIntegrationId = siteIntegrations.data?.find((integration) => integration.type === IntegrationType.CINEMARK && integration.status === IntegrationStatus.ACTIVE)?.id;
  const hasCinemarkIntegration = Boolean(cinemarkIntegrationId);

  const hasLoadedSiteIntegrations = siteIntegrations.status === RequestStatus.SUCCESS && siteIntegrations.data !== undefined;

  const dateParam = new URLSearchParams(search).get('date');
  const date = dateParam && dayjs(dateParam, ['YYYY-MM-DD', 'MM-DD-YYYY'], true).isValid() ? formatDateForApi(dateParam) : undefined;

  const goBackToTipCalculator = () => {
    history.push(routeConstants.private.makePaymentCalculatorDate);
  };

  useEffect(() => {
    if (currentSiteId) {
      dispatch(listSiteIntegrationsRequest(currentSiteId));
      setIntegrationsRequestedFor(currentSiteId);
    }
  }, [currentSiteId]);

  useEffect(() => {
    if (currentSiteId && date && hasLaborDataViewPermission && cinemarkIntegrationId) {
      dispatch(getPosEmployeesRequest(currentSiteId));
      dispatch(getPosJobTitlesRequest(currentSiteId));
      dispatch(getIntegrationLocationRequest(currentSiteId, cinemarkIntegrationId));
      dispatch(getLaborRequest(currentSiteId, date));
    }
  }, [currentSiteId, date, hasLaborDataViewPermission, cinemarkIntegrationId]);

  // Request statuses are shared app-wide and outlive this screen, so clear them on the way out: otherwise the next
  // visit would start from this one's results before its own requests go out.
  useEffect(
    () => () => {
      LABOR_REVIEW_REQUESTS.forEach((request) => dispatch(clearRequest(request)));
    },
    []
  );

  // The rest are only requested once the site's integrations have loaded, so until then their statuses can only be
  // left over from another screen.
  const requestStatuses = [siteIntegrations.status, ...(hasLoadedSiteIntegrations ? [posEmployees.status, posJobs.status, posLocation.status, posLabor.status] : [])];

  // Checked before loading: when the site's integrations fail to load, the rest are never requested.
  const hasFailedRequest = requestStatuses.some((status) => status === RequestStatus.FAILED);
  const isDataLoading = !hasLoadedSiteIntegrations || requestStatuses.some(isRequestLoading);

  // Four independent reasons send an employee home instead of rendering this screen. Each one
  // only fires once its own data has actually loaded — otherwise a valid site would bounce home
  // during the brief window before that data arrives.

  // No valid date also means an unexpected route: the Tip Calculator always links here with the day
  // being paid out. Falling back to another day would show (and let them edit) the wrong day's shifts.
  const hasNoValidDate = !date;

  // No siteId means the employee reached this screen through an unexpected route (not from the Tip
  // Calculator, which always supplies one). currentEmployee can be legitimately unset until the
  // session finishes loading (or, for a non-remote session with no site ever picked, indefinitely),
  // so this waits on the app-level session load rather than on currentEmployee itself.
  const hasNoSiteSelected = !appContext.isLoading && !currentSiteId;

  const lacksPermission = Boolean(currentSite) && !hasLaborDataViewPermission;

  // Site integrations are fetched by this screen, so only redirect for a missing integration once
  // they have loaded (a failed request shows the load error instead).
  const lacksCinemarkIntegration = Boolean(currentSite) && hasLoadedSiteIntegrations && !hasCinemarkIntegration;

  const shouldRedirectHome = hasNoValidDate || hasNoSiteSelected || lacksPermission || lacksCinemarkIntegration;

  useEffect(() => {
    if (shouldRedirectHome) {
      history.replace(routeConstants.private.rootPath);
    }
  }, [history, shouldRedirectHome]);

  if (shouldRedirectHome) return null;

  // Every shift is shown and saved in its location's timezone and business day (labor added for an employee without any,
  // in the primary location's), so the board can't be shown when no locations came back, a record's location is missing
  // from them, or a location lacks either one: it would show wrong times and save edits at the wrong instants. That's the
  // labor response contradicting itself rather than something failing to load, so a refresh won't fix it.
  const locationIds = new Set(posLabor.locations.map((location) => location.id));
  const isLaborComplete = posLabor.locations.length > 0 && posLabor.locations.every((location) => location.timezone && location.businessDayStart) && posLabor.data.every((labor) => locationIds.has(labor.locationId));

  return (
    <Section>
      {hasFailedRequest ? (
        <LaborReviewLoadError message="We couldn't load the labor data for this business day. Please refresh the page to try again." onBackToTipCalculator={goBackToTipCalculator} />
      ) : isDataLoading ? (
        <LoadingContainer data-cy="labor-review-loading">
          <LoaderSpin size={40} stroke={4} />
          <Text fontWeight="semibold">Loading labor data...</Text>
        </LoadingContainer>
      ) : !isLaborComplete ? (
        <LaborReviewLoadError message="This business day's labor data is incomplete, so shifts can't be shown. Please contact Kickfin Support." onBackToTipCalculator={goBackToTipCalculator} />
      ) : (
        <LaborReviewBoard
          siteId={currentSiteId as string}
          date={date}
          locationName={posLocation.data?.name}
          laborRecords={posLabor.data}
          laborLocations={posLabor.locations}
          posEmployees={posEmployees.data}
          jobTitles={jobTitles}
          permissions={currentSite?.permissions}
          onBackToTipCalculator={goBackToTipCalculator}
        />
      )}
    </Section>
  );
};

const isRequestLoading = (status?: RequestStatus) => status === undefined || status === RequestStatus.UNINITIATED || status === RequestStatus.PENDING;

export default LaborReview;
